import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight, Mail } from "lucide-react";
import { useSeo } from "@/hooks/useSeo";
import Navbar from "@/components/ledger/Navbar";
import ScrollProgress from "@/components/ledger/ScrollProgress";
import Footer from "@/components/ledger/Footer";
import FloatingWhatsApp from "@/components/velora/FloatingWhatsApp";
import { LANDINGS, buildLandingJsonLd } from "./data";

/**
 * LandingPage — shared template for the keyword-targeted landing pages.
 *
 * Each route in App.js mounts this component with a `slug` prop that
 * indexes into the LANDINGS map in ./data.js. All content (H1, H2s,
 * product list, FAQ, JSON-LD) comes from that data file so this
 * component stays purely structural.
 *
 * Visual style follows the existing ledger brand — Navbar, Footer,
 * paper background, brass / vermilion accents, Cormorant headlines.
 * No new CSS framework, no extra dependencies.
 */
export default function LandingPage({ slug }) {
  const cfg = LANDINGS[slug];

  // Fail-safe: if a slug is registered as a route but missing from the
  // data map, render a minimal not-found page rather than crashing.
  useSeo({
    title: cfg?.seoTitle || "Vilora Impex",
    description: cfg?.seoDescription,
    path: cfg ? `/${cfg.slug}` : "/",
    image: cfg?.image ? `https://www.viloraimpex.com${cfg.image}` : "https://www.viloraimpex.com/og.jpg",
    imageAlt: cfg?.imageAlt || cfg?.h1,
    jsonLd: cfg ? buildLandingJsonLd(slug) : null,
  });

  if (!cfg) {
    return (
      <main className="ledger relative min-h-screen">
        <Navbar />
        <section className="paper" style={{ padding: "80px 24px" }}>
          <div style={{ maxWidth: 720, margin: "0 auto" }}>
            <h1 className="display" style={{ fontSize: 40 }}>Page not found</h1>
            <p style={{ marginTop: 12 }}>
              <Link to="/" className="story-back-link">Back to home</Link>
            </p>
          </div>
        </section>
        <Footer />
      </main>
    );
  }

  return (
    <main className="ledger landing-page relative min-h-screen" data-testid="landing-page">
      <ScrollProgress />
      <Navbar />

      {/* HERO */}
      <section id="landing-top" className="landing-hero paper relative">
        <div className="landing-shell">
          <nav className="landing-breadcrumb" aria-label="Breadcrumb">
            <Link to="/">Home</Link>
            <span aria-hidden="true">·</span>
            <span aria-current="page">{cfg.h1}</span>
          </nav>

          <div className="landing-folio">
            <span className="folio">{cfg.folio}</span>
            <span className="rule rule-grow" />
            <span className="folio" style={{ color: "var(--ink-mute)" }}>{cfg.sectionLabel}</span>
          </div>

          <span className="brass-ribbon landing-eyebrow">{cfg.eyebrow}</span>

          <h1 className="display landing-h1">{cfg.h1}</h1>

          <p className="landing-standfirst">{cfg.standfirst}</p>

          {cfg.image ? (
            <figure className="landing-hero-media">
              <img
                src={cfg.image}
                alt={cfg.imageAlt || cfg.h1}
                width="1280"
                height="720"
                loading="eager"
                fetchPriority="high"
              />
            </figure>
          ) : null}

          <div className="landing-actions">
            <Link to="/#inquiry" className="btn-primary">
              <span className="btn-code">№ 06</span>
              <span>Open a Conversation</span>
              <span className="btn-arrow">
                <ArrowRight size={14} strokeWidth={1.6} />
              </span>
            </Link>
            <Link to="/catalogue" className="btn-ghost">
              <span>View Full Catalogue</span>
              <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* INTRO */}
      <section className="landing-section paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.introH2}</h2>
          <p className="landing-body">{cfg.introBody}</p>
        </div>
      </section>

      {/* PRODUCTS */}
      <section className="landing-section landing-section--tint paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.productsH2}</h2>
          <ul className="landing-products">
            {cfg.products.map((p) => (
              <li key={p.name} className="landing-product">
                <span className="landing-product-name">{p.name}</span>
                <span className="landing-product-spec">{p.spec}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* PACKAGING & MOQ */}
      <section className="landing-section paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.packagingH2}</h2>
          <p className="landing-body">{cfg.packagingBody}</p>
        </div>
      </section>

      {/* PROCESS */}
      <section className="landing-section landing-section--tint paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">Our export process</h2>
          <ol className="landing-process">
            {cfg.process.map((s, i) => (
              <li key={s.label} className="landing-process-step">
                <span className="landing-process-num">{String(i + 1).padStart(2, "0")}</span>
                <div>
                  <div className="landing-process-label">{s.label}</div>
                  <p className="landing-process-body">{s.body}</p>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* PORTS */}
      <section className="landing-section paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.portsH2}</h2>
          <p className="landing-body">{cfg.portsBody}</p>
        </div>
      </section>

      {/* WHY US */}
      <section className="landing-section landing-section--tint paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.whyH2}</h2>
          <ul className="landing-why">
            {cfg.why.map((w) => (
              <li key={w} className="landing-why-item">{w}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* FAQ */}
      <section className="landing-section paper" id="faq">
        <div className="landing-shell">
          <h2 className="display landing-h2">{cfg.faqsH2}</h2>
          <div className="landing-faqs">
            {cfg.faqs.map((f) => (
              <details key={f.q} className="landing-faq">
                <summary className="landing-faq-q">{f.q}</summary>
                <p className="landing-faq-a">{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CONTACT CTA */}
      <section className="landing-section landing-section--cta paper">
        <div className="landing-shell">
          <h2 className="display landing-h2">Start an inquiry</h2>
          <p className="landing-body">
            Tell us your spec, destination port, packing preference and quantity.
            We reply the same day in our timezone with price, timeline and the
            documents we'll need from you.
          </p>
          <div className="landing-actions">
            <a href="mailto:exports@viloraimpex.com" className="btn-primary">
              <span className="btn-code">EXPORT</span>
              <span>exports@viloraimpex.com</span>
              <span className="btn-arrow">
                <Mail size={14} strokeWidth={1.6} />
              </span>
            </a>
            <Link to="/#inquiry" className="btn-ghost">
              <span>Use the inquiry form</span>
              <span className="btn-arrow">→</span>
            </Link>
          </div>
        </div>
      </section>

      {/* RELATED LANDING PAGES — internal linking */}
      {cfg.related?.length ? (
        <section className="landing-section paper">
          <div className="landing-shell">
            <h2 className="display landing-h2" style={{ fontSize: "clamp(22px, 2.4vw, 28px)" }}>
              Related categories
            </h2>
            <ul className="landing-related">
              {cfg.related.map((r) => {
                const t = LANDINGS[r];
                if (!t) return null;
                return (
                  <li key={r}>
                    <Link to={`/${t.slug}`}>
                      <span>{t.h1}</span>
                      <ArrowUpRight size={14} strokeWidth={1.6} />
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        </section>
      ) : null}

      <Footer />
      <FloatingWhatsApp />
    </main>
  );
}
